/**
 * Public Data Access Layer
 *
 * Provides read-only access to device types, models, and questions from Supabase
 * Used by public frontend components (DeviceSelection, DeviceModelList, DeviceSpecifications)
 *
 * SECURITY NOTE:
 * - Uses only Supabase anon key (public, read-only)
 * - Never uses service_role key
 * - All queries are SELECT only (RLS policies allow public read)
 *
 * FALLBACK:
 * - If Supabase query fails, falls back to static deviceConfig.ts (for device types/models)
 * - Questions have no fallback - returns empty array if query fails
 */

import { supabase } from '../config/supabase';
import { 
  getAllDeviceTypes, 
  getDeviceTypeById, 
  type DeviceCategory as StaticDeviceCategory
} from '../config/deviceConfig';

// Public-facing types for questions and answer options
export type PriceEffectType = 'none' | 'fixed' | 'percent' | 'multiplier' | 'special';

export interface PublicQuestion {
  id: string;
  questionKey: string;
  label: Record<string, string>;
  subtitle?: Record<string, string> | null;
  helpText?: Record<string, string> | null;
  orderIndex: number;
  isRequired: boolean;
}

export interface PublicAnswerOption {
  id: string;
  optionKey: string;
  label: Record<string, string>;
  description?: Record<string, string> | null;
  iconName?: string | null;
  priceEffectType: PriceEffectType;
  priceEffectValue?: number | null;
  orderIndex: number;
}

export interface PublicQuestionWithOptions extends PublicQuestion {
  options: PublicAnswerOption[];
}

// Public-facing types (simplified versions of admin types)
export interface PublicDeviceType {
  id: string; // device_types.key
  name: Record<string, string>; // full JSONB: { nl: "...", en: "...", ... }
  image: string; // image_path or default
  isPopular: boolean;
  hint: Record<string, string>; // full JSONB: { nl: "...", en: "...", ... }
  category: StaticDeviceCategory; // mapped from device_types.category
}

export interface PublicDeviceModel {
  id: string; // device_models.key
  name: string; // display_name[nl] or display_name[en]
  image: string; // image_path or default
  specs: string; // specs[nl] or specs[en] or brand
  isMostChosen: boolean;
  onRequest: boolean;
  filterTags: string[]; // filter_tags array
  availableStorage?: string[]; // keys from available_storage
}

export interface PaginatedModelsResult {
  models: PublicDeviceModel[];
  hasMore: boolean;
  totalCount?: number;
}

function getVariantPriority(key: string): number {
  if (key.includes('-ultra')) return 1;
  if (key.includes('-pro-max')) return 2;
  if (key.includes('-mini')) return 9;
  if (key.includes('-se-')) return 10;
  if (key.includes('-pro')) return 3;
  if (key.includes('-plus')) return 4;
  if (key.includes('-edge')) return 5;
  if (key.includes('-air')) return 7;
  if (key.includes('-fe')) return 8;
  if (/\d+e$/.test(key)) return 11;
  return 6;
}

function getModelNumber(key: string): number {
  const iphone = key.match(/^iphone-(\d+)/);
  if (iphone) return parseInt(iphone[1], 10);
  const samsung = key.match(/galaxy-s(\d+)/);
  if (samsung) return parseInt(samsung[1], 10);
  return 0;
}

/**
 * Get all device types from Supabase
 * Returns full JSONB for multilingual fields (name, hint) — callers resolve language at render time.
 * Sorted by order_index (admin-controlled display order), then key as tiebreaker.
 * Falls back to static config if Supabase query fails.
 */
export async function getPublicDeviceTypes(): Promise<PublicDeviceType[]> {
  try {
    const { data, error } = await supabase
      .from('device_types')
      .select('*')
      .order('order_index', { ascending: true })
      .order('key', { ascending: true });

    if (error) {
      console.warn('Failed to fetch device types from Supabase, using fallback:', error);
      return getFallbackDeviceTypes();
    }

    if (!data || data.length === 0) {
      console.warn('No device types found in Supabase, using fallback');
      return getFallbackDeviceTypes();
    }

    // Map Supabase data to PublicDeviceType format — preserve full JSONB
    return data.map((type) => ({
      id: type.key,
      name: type.display_name || {},
      image: type.image_path || '/devices/default.png',
      isPopular: type.is_popular || false,
      hint: type.hint || {},
      category: mapCategoryToStatic(type.category),
    }));
  } catch (err) {
    console.warn('Exception fetching device types from Supabase, using fallback:', err);
    return getFallbackDeviceTypes();
  }
}

/**
 * Get device models for a specific device type from Supabase (paginated)
 * Uses dynamic lookup: first by key, then by display_name fuzzy match
 *
 * @param deviceTypeKey - Device type key (e.g., "iphone", "samsung")
 * @param language - Language for display names (default: 'nl')
 * @param options.page - Page number (0-indexed, default 0)
 * @param options.limit - Items per page (default 40)
 * @param options.searchTerm - Server-side search; when set, pagination is ignored
 */
export async function getPublicDeviceModelsByType(
  deviceTypeKey: string,
  language: string = 'nl',
  options: { page?: number; limit?: number; searchTerm?: string } = {}
): Promise<PaginatedModelsResult> {
  const { page = 0, limit = 40, searchTerm } = options;
  const normalizedKey = deviceTypeKey.toLowerCase().replace(/\s+/g, '-');

  try {
    // STEP 1: Resolve device_type_id — exact key match, then fuzzy fallback
    let { data: deviceType, error: typeError } = await supabase
      .from('device_types')
      .select('id, key, display_name')
      .ilike('key', normalizedKey)
      .maybeSingle();

    if (!deviceType && !typeError) {
      const { data: fuzzyMatch } = await supabase
        .from('device_types')
        .select('id, key, display_name')
        .or(`display_name->>nl.ilike.%${deviceTypeKey}%,display_name->>en.ilike.%${deviceTypeKey}%,key.ilike.%${deviceTypeKey.replace(/\s+/g, '%')}%`)
        .limit(1)
        .maybeSingle();

      if (fuzzyMatch) deviceType = fuzzyMatch;
    }

    if (typeError || !deviceType) {
      return { models: [], hasMore: false };
    }

    // STEP 2a: Get total count (cheap head:true query)
    const { count: totalCount } = await supabase
      .from('device_models')
      .select('id', { count: 'exact', head: true })
      .eq('device_type_id', deviceType.id);

    // STEP 2b: Build models query with server-side ordering
    // Sort: is_most_chosen DESC → release_year DESC (nulls last) → key ASC
    let query = supabase
      .from('device_models')
      .select('key, display_name, image_path, specs, brand, is_most_chosen, on_request, filter_tags, available_storage, release_year')
      .eq('device_type_id', deviceType.id)
      .order('is_most_chosen', { ascending: false })
      .order('release_year', { ascending: false, nullsFirst: false })
      .order('key', { ascending: true });

    const isSearching = !!(searchTerm && searchTerm.trim());

    if (isSearching) {
      // Server-side search: filter across display_name languages and key, no pagination
      const term = searchTerm!.trim();
      query = query.or(
        `display_name->>nl.ilike.%${term}%,display_name->>en.ilike.%${term}%,key.ilike.%${term}%`
      );
    } else {
      // Paginated: fetch limit + 1 to detect hasMore
      const from = page * limit;
      const to = from + limit; // inclusive — returns up to limit + 1 rows
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error || !data) {
      return { models: [], hasMore: false };
    }

    let hasMore = false;
    let resultData = data;

    if (!isSearching && data.length > limit) {
      hasMore = true;
      resultData = data.slice(0, limit);
    }

    // Sort: release_year DESC → variant priority ASC → model number DESC
    resultData.sort((a: any, b: any) => {
      const yearDiff = (b.release_year ?? 0) - (a.release_year ?? 0);
      if (yearDiff !== 0) return yearDiff;
      const varDiff = getVariantPriority(a.key) - getVariantPriority(b.key);
      if (varDiff !== 0) return varDiff;
      return getModelNumber(b.key) - getModelNumber(a.key);
    });

    const models = resultData.map((model) => ({
      id: model.key,
      name: model.display_name?.[language] || model.display_name?.en || model.display_name?.nl || model.key,
      image: model.image_path || '/devices/default.png',
      specs: model.specs?.[language] || model.specs?.en || model.specs?.nl || model.brand || '',
      isMostChosen: model.is_most_chosen || false,
      onRequest: model.on_request || false,
      filterTags: model.filter_tags || [],
      availableStorage: model.available_storage || [],
    }));

    return { models, hasMore, totalCount: totalCount ?? undefined };
  } catch (err) {
    console.error(`[getPublicDeviceModelsByType] Exception for "${deviceTypeKey}":`, err);
    return { models: [], hasMore: false };
  }
}

export async function getPublicStorageOptionsByModelKey(deviceModelKey: string): Promise<string[]> {
  if (!deviceModelKey) return [];

  try {
    // Fetch all unique storage keys from the price matrix for this model
    // Join with device_models to filter by model key directly
    const { data: matrixRows, error: matrixError } = await supabase
      .from('device_price_matrix')
      .select('storage_key, device_models!inner(key)')
      .eq('device_models.key', deviceModelKey);

    if (matrixError) {
      console.warn(`[getPublicStorageOptionsByModelKey] Error fetching price matrix for model "${deviceModelKey}":`, matrixError);
      return [];
    }
    
    // Debug log for MacBook Air 16TB check
    if (deviceModelKey.includes('macbook') || deviceModelKey.includes('air')) {
       console.log('[STORAGE_OPTIONS_BY_MODEL]', { deviceModelKey, rows: matrixRows });
    }

    if (!matrixRows || matrixRows.length === 0) {
      return [];
    }

    // Extract, clean, unique-ify, and sort the storage keys
    // We'll use a custom sort to handle GB vs TB correctly (e.g. 1TB > 512GB)
    const uniqueKeys = Array.from(
      new Set(
        matrixRows
          .map((row) => row.storage_key)
          .filter((key) => typeof key === 'string' && key.trim().length > 0)
      )
    );

    // Helper to parse storage size for sorting
    const parseStorage = (s: string) => {
      const match = s.match(/^(\d+)\s*(GB|TB)$/i);
      if (!match) return { size: 0, unit: '' };
      return { size: parseInt(match[1], 10), unit: match[2].toUpperCase() };
    };

    return uniqueKeys.sort((a, b) => {
      const valA = parseStorage(a);
      const valB = parseStorage(b);
      
      // If units are different, TB > GB
      if (valA.unit !== valB.unit) {
        if (valA.unit === 'TB') return 1;
        if (valB.unit === 'TB') return -1;
        return 0; // unknown units
      }
      
      // Same unit, compare sizes
      return valA.size - valB.size;
    });

  } catch (err) {
    console.warn(`[getPublicStorageOptionsByModelKey] Exception:`, err);
    return [];
  }
}

/**
 * Get a single device model by its key
 * Used to fetch detailed specs like available_storage for DeviceSpecifications
 */
export async function getPublicDeviceModelByKey(
  modelKey: string,
  language: string = 'nl'
): Promise<PublicDeviceModel | null> {
  try {
    const { data: model, error } = await supabase
      .from('device_models')
      .select('key, display_name, image_path, specs, brand, is_most_chosen, on_request, filter_tags, available_storage')
      .eq('key', modelKey)
      .maybeSingle();

    if (error) {
      console.warn(`Failed to fetch model "${modelKey}":`, error);
      return null;
    }

    if (!model) {
      return null;
    }

    return {
      id: model.key,
      name: model.display_name?.[language] || model.display_name?.en || model.display_name?.nl || model.key,
      image: model.image_path || '/devices/default.png',
      specs: model.specs?.[language] || model.specs?.en || model.specs?.nl || model.brand || '',
      isMostChosen: model.is_most_chosen || false,
      onRequest: model.on_request || false,
      filterTags: model.filter_tags || [],
      availableStorage: model.available_storage || [],
    };
  } catch (err) {
    console.warn(`Exception fetching model "${modelKey}":`, err);
    return null;
  }
}

/**
 * Map Supabase category to static DeviceCategory type
 */
function mapCategoryToStatic(category: string): StaticDeviceCategory {
  const categoryMap: Record<string, StaticDeviceCategory> = {
    phone: 'Phone',
    tablet: 'Tablet',
    laptop: 'Laptop',
    desktop: 'Desktop',
    watch: 'Watch',
    other: 'Other',
  };
  return categoryMap[category.toLowerCase()] || 'Other';
}

/**
 * Fallback: Get device types from static config
 * Wraps single-language (Dutch) strings into Record<string, string> for consistent typing.
 */
function getFallbackDeviceTypes(): PublicDeviceType[] {
  const staticTypes = getAllDeviceTypes();
  return staticTypes.map((type) => ({
    id: type.id,
    name: { nl: type.name },
    image: type.image,
    isPopular: type.isPopular || false,
    hint: { nl: type.hint || '' },
    category: type.category,
  }));
}

/**
 * Fallback: Get device models from static config
 */
function getFallbackDeviceModels(deviceTypeKey: string, language: string): PublicDeviceModel[] {
  console.warn(`[FALLBACK ACTIVE] Using static deviceConfig for "${deviceTypeKey}" - Supabase data not available`);

  const staticType = getDeviceTypeById(deviceTypeKey);
  if (!staticType) {
    console.error(`[FALLBACK FAILED] No static config found for "${deviceTypeKey}" either!`);
    return [];
  }

  console.log(`[FALLBACK] Returning ${staticType.models.length} models from static config for "${deviceTypeKey}"`);

  return staticType.models.map((model) => ({
    id: model.id,
    name: model.name,
    image: model.image,
    specs: model.specs,
    isMostChosen: model.isMostChosen || false,
    onRequest: model.onRequest || false,
    filterTags: model.filterTags || [],
  }));
}

/**
 * Get public questions with answer options for a device type
 * Used by DeviceSpecifications component to dynamically render question blocks
 * 
 * @param deviceTypeKey - device_types.key (e.g., "iphone", "macbook", "apple-watch")
 * @returns Array of questions with their answer options, ordered by order_index
 */
export async function getPublicQuestionsWithOptions(
  deviceTypeKey: string
): Promise<PublicQuestionWithOptions[]> {
  // Normalize the key: lowercase AND replace spaces with hyphens
  const normalizedKey = deviceTypeKey.toLowerCase().replace(/\s+/g, '-');

  try {
    // Step 1: Get device_type_id from device_types table using key (case-insensitive)
    const { data: deviceType, error: typeError } = await supabase
      .from('device_types')
      .select('id, key')
      .ilike('key', normalizedKey)
      .maybeSingle();

    if (typeError) {
      console.error('[DEVICE TYPE ERROR]', {
        deviceTypeKey,
        normalizedKey,
        errorMessage: typeError.message,
        errorCode: typeError.code,
        hint: typeError.hint,
        details: typeError.details,
      });
      return [];
    }

    if (!deviceType) {
      console.warn('[DEVICE TYPE NOT FOUND]', { deviceTypeKey, normalizedKey });
      console.warn('[QUESTIONS FETCH SKIPPED] Device type not found for key', normalizedKey);
      return [];
    }

    // Step 2: Get questions for this device type
    // Ordered by order_index ASC (nulls first), then by created_at ASC
    const { data: questions, error: questionsError } = await supabase
      .from('questions')
      .select('id, question_key, label, subtitle, help_text, order_index, is_required, created_at')
      .eq('device_type_id', deviceType.id)
      .order('order_index', { ascending: true, nullsFirst: true })
      .order('created_at', { ascending: true });

    // Debug: Log raw questions from Supabase to verify order
    console.log('[DB QUESTIONS RAW]', questions?.map(q => ({
      id: q.id,
      question_key: q.question_key,
      order_index: q.order_index,
      created_at: q.created_at,
    })));

    if (questionsError) {
      console.warn(`Failed to fetch questions for "${deviceTypeKey}":`, questionsError);
      return [];
    }

    if (!questions || questions.length === 0) {
      // No questions defined for this device type - return empty array
      return [];
    }

    // Step 3: Get all answer options for these questions in a single query
    const questionIds = questions.map((q) => q.id);
    const { data: answerOptions, error: optionsError } = await supabase
      .from('answer_options')
      .select('id, question_id, option_key, label, description, icon_name, price_effect_type, price_effect_value, order_index')
      .in('question_id', questionIds)
      .order('order_index', { ascending: true })
      .order('id', { ascending: true });

    if (optionsError) {
      console.warn(`Failed to fetch answer options for "${deviceTypeKey}":`, optionsError);
      // Return questions without options rather than failing completely
      return questions.map((q) => ({
        id: q.id,
        questionKey: q.question_key,
        label: q.label || {},
        subtitle: q.subtitle || null,
        helpText: q.help_text || null,
        orderIndex: q.order_index || 0,
        isRequired: q.is_required || false,
        options: [],
      }));
    }

    // Step 4: Group answer options by question_id
    const optionsByQuestionId: Record<string, PublicAnswerOption[]> = {};
    if (answerOptions) {
      for (const option of answerOptions) {
        if (!optionsByQuestionId[option.question_id]) {
          optionsByQuestionId[option.question_id] = [];
        }
        optionsByQuestionId[option.question_id].push({
          id: option.id,
          optionKey: option.option_key,
          label: option.label || {},
          description: option.description || null,
          iconName: option.icon_name || null,
          priceEffectType: (option.price_effect_type || 'none') as PriceEffectType,
          priceEffectValue: option.price_effect_value ?? null,
          orderIndex: option.order_index || 0,
        });
      }
    }

    // Step 5: Map to PublicQuestionWithOptions format
    // IMPORTANT: Preserve the exact order from Supabase query (already sorted by order_index, created_at)
    const mappedQuestions = questions.map((q) => ({
      id: q.id,
      questionKey: q.question_key,
      label: q.label || {},
      subtitle: q.subtitle || null,
      helpText: q.help_text || null,
      orderIndex: q.order_index || 0,
      isRequired: q.is_required || false,
      options: optionsByQuestionId[q.id] || [],
    }));

    // Debug: Log mapped questions to verify order is preserved
    console.log('[MAPPED QUESTIONS ORDER]', mappedQuestions.map(q => ({
      id: q.id,
      key: q.questionKey,
      orderIndex: q.orderIndex,
    })));

    return mappedQuestions;
  } catch (err) {
    console.warn(`Exception fetching questions for "${deviceTypeKey}":`, err);
    return [];
  }
}

/**
 * Get sales questions with answer options for a specific device MODEL
 * Used by DeviceSpecifications component when Sales Templates are applied to models
 *
 * This fetches from the sales_device_questions and sales_device_answer_options tables,
 * which are populated when a Sales Template is applied to a device model.
 *
 * @param modelKey - device_models.key (e.g., "macbook-air-m1-2020", "iphone-15-pro")
 * @returns Array of questions with their answer options, ordered by order_index
 */
export async function getPublicSalesQuestionsForModel(
  modelKey: string
): Promise<PublicQuestionWithOptions[]> {
  if (!modelKey) {
    return [];
  }

  try {
    // Step 1: Get device_model.id from device_models table using key
    const { data: deviceModel, error: modelError } = await supabase
      .from('device_models')
      .select('id, key')
      .eq('key', modelKey)
      .maybeSingle();

    if (modelError) {
      console.warn(`[SALES QUESTIONS] Failed to find model "${modelKey}":`, modelError);
      return [];
    }

    if (!deviceModel) {
      console.log(`[SALES QUESTIONS] Model "${modelKey}" not found in device_models`);
      return [];
    }

    // Step 2: Call the RPC function to get sales questions for this model
    const { data: rpcResult, error: rpcError } = await supabase
      .rpc('get_sales_questions_for_model', { p_device_model_id: deviceModel.id });

    if (rpcError) {
      console.warn(`[SALES QUESTIONS] RPC error for model "${modelKey}":`, rpcError);
      return [];
    }

    // The RPC returns JSON directly - it could be an array or null
    const questionsData = rpcResult as any[] | null;

    if (!questionsData || questionsData.length === 0) {
      console.log(`[SALES QUESTIONS] No sales questions found for model "${modelKey}"`);
      return [];
    }

    console.log(`[SALES QUESTIONS] Found ${questionsData.length} questions for model "${modelKey}"`);

    // Step 3: Map RPC result to PublicQuestionWithOptions format
    const mappedQuestions: PublicQuestionWithOptions[] = questionsData.map((q: any) => ({
      id: q.id,
      questionKey: q.question_key,
      label: q.label || {},
      subtitle: q.subtitle || null,
      helpText: q.help_text || null,
      orderIndex: q.order_index || 0,
      isRequired: q.is_required || false,
      options: (q.options || []).map((opt: any) => ({
        id: opt.id,
        optionKey: opt.option_key,
        label: opt.label || {},
        description: opt.description || null,
        iconName: opt.icon_name || null,
        priceEffectType: (opt.price_effect_type || 'none') as PriceEffectType,
        priceEffectValue: opt.price_effect_value ?? null,
        orderIndex: opt.order_index || 0,
      })),
    }));

    // Debug: Log mapped questions
    console.log('[SALES QUESTIONS MAPPED]', mappedQuestions.map(q => ({
      id: q.id,
      key: q.questionKey,
      orderIndex: q.orderIndex,
      optionCount: q.options.length,
    })));

    return mappedQuestions;
  } catch (err) {
    console.warn(`[SALES QUESTIONS] Exception for model "${modelKey}":`, err);
    return [];
  }
}

